import React, { useEffect, useState } from "react";
import styles from "./PhotoViewerModal.module.scss";
import { Image, message, Modal, Spin } from "antd";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import Fallback from "../../../../assets/images/image-fallback.svg";
import DownloadAllIcon from "../../../../assets/images/download_icon.svg";
import DownloadIcon from "../../../../assets/images/download-white.svg";
import DeleteIcon from "../../../../assets/images/delete-white.svg";
import {
  EyeOutlined,
  LeftCircleFilled,
  RightCircleFilled,
} from "@ant-design/icons";
import { photoModalConstants } from "./constants";
import Label from "../../../../reusable-components/Label";
import {
  ButtonShape,
  ButtonSize,
} from "../../../../enums/ButtonAttributesEnums";
import TNButton from "../../../../reusable-components/TNButton";
import PhotoViewerDragger from "./PhotoViewerUpload";
import QuarryDatabaseService from "../../../../services/quarryDatabase.service";
import { downloadUtil } from "../../../../utils/downloadUtil";
import TNLoader from "../../../../reusable-components/TNLoader";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../../constants/QueryKeys";
import clsx from "clsx";

interface PhotoViewerModalProps {
  isOpen?: boolean;
  handleClose: () => void;
  quarryId: string;
  materialId: string;
}

const PhotoViewerModal = ({
  isOpen,
  handleClose,
  quarryId,
  materialId,
}: PhotoViewerModalProps) => {
  const {
    WIDTH,
    TITLE,
    SUBMIT_TXT,
    PICTURES_LABEL,
    DOWNLOAD_ALL,
    BUTTON_CLASSNAME,
    UPLOAD_SUCCESS,
    DELETE_SUCCESS,
    DOWNLOAD_ALT,
    DELETE_ALT,
    NO_IMAGES,
    UPLOAD_EMPTY_ERROR,
  } = photoModalConstants;

  const {
    GetMaterialImages,
    DownloadAllMaterialImages,
    DeleteMaterialImage,
    AddMaterialPhotos,
  } = QuarryDatabaseService();
  const {
    data: photoList,
    isPending,
    isFetching,
    isRefetching,
    isLoading,
    refetch,
  } = GetMaterialImages({
    quarryId: quarryId,
    materialId: materialId,
    enabled: isOpen,
  });
  const queryClient = useQueryClient();
  const { mutateAsync: addImages, isPending: addImagePending } =
    AddMaterialPhotos();
  const { mutateAsync: downloadAll, isPending: downloadAllPending } =
    DownloadAllMaterialImages();
  const { mutateAsync: deleteImage, isPending: deletePending } =
    DeleteMaterialImage({
      quarryId: quarryId,
    });

  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
  const [viewerImage, setViewerImage] = useState<number>(0);
  const [showFallback, setFallback] = useState<boolean>(false);
  const [loaderState, setLoaderState] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [fileAdded, setFileAdded] = useState(false);
  const handleSetIsViewerOpen = () => setIsViewerOpen(true);
  const handleSetIsViewerClose = () => setIsViewerOpen(false);
  const handleCloseFunction = () => {
    setUploadedFiles([]);
    handleClose();
  };
  const handleSetViewerImage = (index: number) => {
    setViewerImage(index);
    handleSetIsViewerOpen();
  };

  const handleGoPrevious = () => {
    setViewerImage((prev) => prev - 1);
  };

  const handleGoForward = () => {
    setViewerImage((prev) => prev + 1);
  };

  const handleDownloadImage = async () => {
    setLoaderState(true);
    try {
      const urlLink = photoList?.[viewerImage]?.url?.split("?")?.[0];
      await downloadUtil(urlLink);
    } catch (err) {
      console.error("Err", err);
    } finally {
      setLoaderState(false);
    }
  };

  const handleDeleteImage = async () => {
    const payload = {
      materialId: materialId,
      entryId: photoList?.[viewerImage]?.id,
    };
    const deletedId = photoList?.[viewerImage]?.id;
    await deleteImage(payload, {
      onSuccess: () => {
        setViewerImage(0);
        handleSetIsViewerClose();
        message.success(DELETE_SUCCESS);
        queryClient.setQueryData(
          [QueryKeys.GET_MATERIAL_IMAGES_KEY],
          (oldData: any[]) => {
            if (!oldData) return [];
            return oldData?.filter((item) => item?.id !== deletedId);
          }
        );
      },
    });
  };

  const handleFilesUploaded = (files: any[], isUpload: boolean) => {
    if (isUpload) {
      setUploadedFiles((prev) => {
        const newArray = [...prev, ...files];
        const filteredArray = newArray?.filter(
          (item, index, self) =>
            index === self?.findIndex((t) => t?.uid === item?.uid)
        );
        return filteredArray;
      });
    } else {
      setUploadedFiles(files);
    }
  };

  const handleDownloadAll = async () => {
    setLoaderState(true);
    const data = await downloadAll({
      quarryId: quarryId,
      materialId: materialId,
    });
    await downloadUtil(data);
    setLoaderState(false);
  };

  const handleAddMaterialImages = async () => {
    const payload = {
      quarryId: quarryId,
      materialId: materialId,
      materialPicture: uploadedFiles?.map(({ uid, ...rest }) => rest),
    };
    await addImages(payload, {
      onSuccess: () => {
        setUploadedFiles([]);
        message.success(UPLOAD_SUCCESS);
        refetch();
      },
    });
  };

  return (
    <>
      <TNLoader
        isLoading={
          downloadAllPending || loaderState || deletePending || addImagePending
        }
      />
      <Modal
        visible={isViewerOpen}
        className={styles.viewerModal}
        footer={null}
        maskClosable={true}
        onCancel={handleSetIsViewerClose}
        destroyOnClose
        closable={false}
      >
        <div className={styles.imageContainer}>
          <LeftCircleFilled
            className={clsx(styles.directionButton, {
              [styles.removeButton]: viewerImage === 0,
            })}
            onClick={(e) => {
              e.stopPropagation();
              handleGoPrevious();
            }}
          />
          <div className={styles.imageWrapper}>
            <div className={styles.imageView}>
              <img
                src={photoList?.[viewerImage]?.url}
                alt=""
                className={styles.imageStyle}
              />
              {photoList?.[viewerImage]?.url && (
                <div className={styles.imageActions}>
                  <div
                    className={styles.actionContainer}
                    onClick={handleDownloadImage}
                  >
                    <img src={DownloadIcon} alt={DOWNLOAD_ALT} />
                  </div>
                  <div
                    className={styles.actionContainer}
                    onClick={handleDeleteImage}
                  >
                    <img src={DeleteIcon} alt={DELETE_ALT} />
                  </div>
                </div>
              )}
            </div>
          </div>
          <RightCircleFilled
            className={clsx(styles.directionButton, {
              [styles.removeButton]:
                viewerImage === photoList?.length - 1 ||
                photoList?.length === 1,
            })}
            onClick={(e) => {
              e.stopPropagation();
              handleGoForward();
            }}
          />
        </div>
      </Modal>
      <Modal
        visible={isOpen}
        onCancel={handleCloseFunction}
        onOk={() => {
          if (uploadedFiles?.length > 0) handleAddMaterialImages();
          else message.error(UPLOAD_EMPTY_ERROR);
        }}
        destroyOnClose
        maskClosable={false}
        width={WIDTH}
        title={TITLE}
        className={styles.photoViewerModal}
        okText={SUBMIT_TXT}
      >
        <ConditionalRender
          fallback={
            <Spin className={styles.loaderStyle} size={ButtonSize.LARGE} />
          }
          visible={!(isPending || isFetching || isRefetching || isLoading)}
        >
          <div className={styles.container}>
            <div className={styles.imagesContainer}>
              <div className={styles.imagesHeader}>
                <Label label={PICTURES_LABEL} />
                <TNButton
                  size={ButtonSize.MEDIUM}
                  shape={ButtonShape.DEFAULT}
                  className={BUTTON_CLASSNAME}
                  disabled={photoList?.length === 0}
                  onClick={() => {
                    handleDownloadAll();
                  }}
                  text={
                    <>
                      <img
                        className={styles.downloadIcon}
                        src={DownloadAllIcon}
                        alt={""}
                      />
                      <p className={styles.downloadText}>{DOWNLOAD_ALL}</p>
                    </>
                  }
                />
              </div>
              <ConditionalRender
                fallback={
                  <div className={styles.emptyContainer}>{NO_IMAGES}</div>
                }
                visible={photoList?.length !== 0}
              >
                <div className={styles.imagesWrapper} key={photoList?.length}>
                  {photoList?.map((image, index) => {
                    if (index < 4) {
                      return (
                        <Image
                          fallback={Fallback}
                          src={image?.url}
                          preview={{
                            visible: false,
                            mask: (
                              <div
                                className={styles.customImageMask}
                                onClick={() => {
                                  handleSetViewerImage(index);
                                }}
                              >
                                <EyeOutlined style={styles.EyeContainer} />
                              </div>
                            ),
                          }}
                          className={styles.customImage}
                        />
                      );
                    } else if (index === 4) {
                      return (
                        <Image
                          onError={(e) => {
                            e.currentTarget.src = Fallback;
                            e.currentTarget.style.width = "100%";
                            e.currentTarget.style.height = "100%";
                          }}
                          src={image?.url}
                          preview={{
                            visible: false,
                            mask: (
                              <div
                                className={styles.customImageMask}
                                onClick={() => {
                                  handleSetViewerImage(index);
                                }}
                              >
                                {photoList?.length > 5 ? (
                                  `${photoList?.length - (index + 1)}+ more`
                                ) : (
                                  <EyeOutlined style={styles.EyeContainer} />
                                )}
                              </div>
                            ),
                          }}
                          className={styles.customImage}
                        />
                      );
                    } else {
                      return <></>;
                    }
                  })}
                </div>
              </ConditionalRender>
            </div>
            <div>
              <PhotoViewerDragger
                docType={""}
                quarryId={quarryId}
                materialId={materialId}
                filesUploaded={uploadedFiles}
                maxFileSize={10}
                onFilesUploaded={handleFilesUploaded}
                setFileAdded={setFileAdded}
              />
            </div>
          </div>
        </ConditionalRender>
      </Modal>
    </>
  );
};

export default PhotoViewerModal;
